import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "../../../atoms/CustomButton";
import useAuth from "../../../../hooks/useAuth";
import { useCheckBadgesVerify } from "../../../../hooks/entity";
import LoyaltyBadge from "../../../atoms/LoyaltyBadge";
import { LoyaltyBadgeType } from "../../../../utils/loyaltyBadgeUtils";
import { HiCheckBadge } from "react-icons/hi2";
import { HiShieldCheck } from "react-icons/hi";
import BadgeVerificationErrorModal from "../../../modals/BadgeVerificationErrorModal";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

interface BadgeVerificationProps {
  badges?: string[];
}

const BadgeVerification: React.FC<BadgeVerificationProps> = ({ badges }) => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const [showErrorModal, setShowErrorModal] = useState(false);

  const { mutate: checkVerifyBadges, isLoading: isCheckLoading } =
    useCheckBadgesVerify(() => setShowErrorModal(true));

  const isVerified = badges?.includes("verified");

  const permissions = usePermissions(user);
  const canUpdateLoyalty = permissions.can(Cap.LOYALTY, Level.WRITE);

  const verifyBadges = () => {
    if (!canUpdateLoyalty) return;
    checkVerifyBadges({
      entity_identification: user.entity_identification,
      customer_identification: customer_identification,
      user_identification: user.user_identification,
    });
  };

  const verifiedBadge: LoyaltyBadgeType = {
    type: "verified",
    label: t("dashboard.features.lmc.badgeVerification.verifiedButton"),
    icon: "verified",
  };

  return (
    <div className="w-full mt-6">
      <div className="flex justify-center mb-6">
        <LoyaltyBadge badge={verifiedBadge} size="lg" textColor="gray" />
      </div>

      {!isVerified && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-white border border-gray-200 rounded-md flex items-center justify-center flex-shrink-0">
                <HiShieldCheck size={18} className="text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900">
                  {t("dashboard.features.lmc.badgeVerification.infoBoxTitle")}
                </h4>
                <p className="mt-0.5 text-xs text-gray-500">
                  {t(
                    "dashboard.features.lmc.badgeVerification.infoBoxSubtitle"
                  )}
                </p>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-3 text-xs">
              <p className="mb-2 font-semibold text-gray-900">
                {t(
                  "dashboard.features.lmc.badgeVerification.requirementsTitle"
                )}
              </p>
              <ol className="flex flex-col gap-1.5 text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="tabular-nums text-gray-400">1.</span>
                  <span>
                    {t("dashboard.features.lmc.badgeVerification.requirement1")}
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="tabular-nums text-gray-400">2.</span>
                  <span>
                    {t("dashboard.features.lmc.badgeVerification.requirement2")}
                  </span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="tabular-nums text-gray-400">3.</span>
                  <span>
                    {t("dashboard.features.lmc.badgeVerification.requirement3")}
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center mb-4">
        <HiCheckBadge size={18} className="text-gray-400 mr-2" />
        <h3 className="text-base font-semibold text-gray-900">
          {t("dashboard.features.lmc.badgeVerification.title")}
        </h3>
      </div>

      {canUpdateLoyalty && (
        <div className="mb-6">
          <div
            className={`p-4 rounded-lg border ${
              isVerified
                ? "bg-green-50 border-green-200"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            {isVerified ? (
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-green-100 border border-green-300 rounded-lg">
                  <svg
                    className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <p className="text-green-800 text-sm font-semibold">
                      {t(
                        "dashboard.features.lmc.badgeVerification.alreadyVerified"
                      )}
                    </p>
                    <p className="text-green-700 text-xs mt-1">
                      {t(
                        "dashboard.features.lmc.badgeVerification.verifiedDescription"
                      )}
                    </p>
                  </div>
                </div>
                <CustomButton
                  disabled={true}
                  onClick={verifyBadges}
                  title={t(
                    "dashboard.features.lmc.badgeVerification.verifiedButton"
                  )}
                  className="bg-green-500 enabled:hover:bg-green-500 text-white flex-row-reverse border-none w-full"
                  variant="outline"
                  icon="tick"
                />
              </div>
            ) : (
              <CustomButton
                disabled={isCheckLoading || !canUpdateLoyalty}
                isLoading={isCheckLoading}
                onClick={verifyBadges}
                title={t(
                  "dashboard.features.lmc.badgeVerification.requestButton"
                )}
                className="bg-blue-500 enabled:hover:bg-blue-600 border-none w-full text-white"
                variant="outline"
              />
            )}
          </div>
        </div>
      )}

      <BadgeVerificationErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default React.memo(BadgeVerification);
